import {
  MDBContainer,
  MDBBtn,
  MDBModal,
  MDBModalBody,
  MDBModalFooter,
  MDBRow,
  MDBCol,
  MDBIcon,
} from "mdbreact";
import React, { useEffect, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { AppContainer } from "../../../containers/App";
import { CampaignContainer } from "../../../containers/Campaign";
import { ErrorContainer } from "../../../containers/Error";
import { StampCardContainer } from "../../../containers/StampCard";
import { Stamp } from "../../../containers/Stamps";
import { UserContainer } from "../../../containers/User";
import CampaignImage from "../../elements/campaign-image/CampaignImage";
import AchievementStepper from "../../modules/achievement-stepper/AchievementStepper";
import StampCard from "../../modules/stamp-card/StampCard";
import TenantDescriptionModal from "../../modules/tenant-description-modal/TenantDescriptionModal";
import TextDecoration from "../../modules/text-decoration/TextDecoration";
import classes from "./StampCardPage.module.scss";

/**
 * スタンプカード画面コンポーネント
 */
const StampCardPage: React.FC<
  RouteComponentProps<{
    campaignId: string;
    id: string;
  }>
> = (props) => {
  // スタンプ達成モーダルの展開状態
  const [isQuestionnaireIntroModalOpen, setIsQuestionnaireIntroModalOpen] =
    useState<boolean>(false);
  // テナント詳細情報モーダルの展開状態
  const [isTenantDescriptionOpen, setIsTenantDescriptionOpen] =
    useState<boolean>(false);
  // クリックされたスタンプ
  const [selectedStamp, setSelectedStamp] = useState<Stamp>();

  const history = useHistory();
  const appContainer = AppContainer.useContainer();
  const errorContainer = ErrorContainer.useContainer();
  const userContainer = UserContainer.useContainer();
  const campaignContainer = CampaignContainer.useContainer();
  const stampCardContainer = StampCardContainer.useContainer();

  const user = userContainer.user;
  const userId = user.id || "";

  const campaign = campaignContainer.campaign;
  const campaignId = campaign?.id || "";

  const stampCard = stampCardContainer.stampCard;
  const stamps = stampCard?.stamps;

  if (
    (!stampCard || !stampCard.isLatest) &&
    user.id &&
    user.token &&
    !appContainer.context.loading
  ) {
    // スタンプカードの初期化
    appContainer.setLoading(true);
    stampCardContainer
      .initStampCard(user.id, campaignId, user.token)
      .then(() => appContainer.setLoading(false))
      .catch((error) => errorContainer.setDefaultError(userId, error));
  }

  const achieveCount = stamps?.filter((s) => s.isAchieve).length;
  const checkedCount = stamps?.filter((s) => s.isChecked).length;

  const isAchieved = achieveCount === 4;
  const isChecked = checkedCount === 4;
  const isAnswered = stampCard?.questionnaireStatus.isAnswered;

  useEffect(() => {
    if (!user.token) {
      // 未ログインの場合、ログインページに遷移
      history.push("/login");
    }

    if (!stampCard) {
      return;
    }

    if (!stampCard.isEntry) {
      // 未エントリーであれば、エントリー画面に遷移
      history.push(`/entry`);
      return;
    }

    if (!stamps) {
      // スタンプ未選択であれば、テナント選択画面に遷移
      history.push(`/tenant-select`);
      return;
    }

    if (isAchieved && !isChecked && !isAnswered) {
      // スタンプ達成済み かつ スタンプ付与アニメを見終えていない かつ アンケート未回答 の場合は
      // アンケート画面に遷移するためのモーダルを展開
      setTimeout(() => {
        setIsQuestionnaireIntroModalOpen(true);
      }, 1200);
      return;
    }
  }, [
    history,
    stampCard,
    stamps,
    user.token,
    isAchieved,
    isChecked,
    isAnswered,
  ]);

  /**
   * テナントスタンプ変更ボタンクリック時の処理
   */
  const handleTenantReplaceClick = async () => {
    const categories = campaign?.categories.map((c) => c.category);

    if (!user.id || !user.token || !categories) {
      return;
    }

    try {
      appContainer.setLoading(true);
      history.push(`/tenant-select`);
      await stampCardContainer.recommend(
        user.id,
        campaignId,
        categories,
        user.token
      );
    } catch (error) {
      errorContainer.setDefaultError(userId, error);
    } finally {
      appContainer.setLoading(false);
    }
  };

  /**
   * 「アンケートに答える」ボタンクリック時の処理
   */
  const handleQuestionnaireButtonClick = () => {
    history.push(`/questionnaire/${userId}`);
  };

  /**
   * アンケート遷移ダイアログの「閉じる」ボタンクリック時の処理
   */
  const handleCloseButtonClick = () => {
    setIsQuestionnaireIntroModalOpen(false);
    // isChecked反映のため、StampCards を初期化
    if (user.id && user.token) {
      appContainer.setLoading(true);
      stampCardContainer
        .initStampCard(user.id, campaignId, user.token)
        .then(() => appContainer.setLoading(false))
        .catch((error) => errorContainer.setDefaultError(userId, error));
    }
  };

  /**
   * スタンプシートのスタンプをクリックしたときの処理
   * @param id スタンプID
   */
  const handleStampClick = (id: string) => {
    const stamp = stamps?.find((s) => s.id === id);
    if (!stamp) {
      return;
    }

    setSelectedStamp(stamp);
    setIsTenantDescriptionOpen(true);
  };

  const questionnaire = campaign?.subContents.questionnaire;

  return (
    <MDBContainer>
      <CampaignImage className="my-3" image={campaign?.image || ""} />
      <div className={`${classes.description} mt-3`}>
        <div className={`${classes.mainText} text-center mb-3`}>
          {campaign?.status === "end" ? (
            // キャンペーン終了後
            <TextDecoration sentences={campaign?.finishedDescription} />
          ) : !isAchieved ? (
            // スタンプコンプリート前
            <React.Fragment>
              <div className={`text-center mt-4 ${classes.stepMessage}`}>
                <div className={`${classes.title}`}>STEP3</div>
                <div className={classes.text}>
                  お買物をしてスタンプを集めましょう
                </div>
              </div>
              <div
                className={`mt-3 py-2 px-3 d-inline-block ${classes.caution}`}
              >
                <MDBIcon
                  icon="exclamation-circle"
                  className="mr-1 mr-sm-2"
                ></MDBIcon>
                お買物の際に、必ずポイントカードまたは<br></br>
                ポイント用QRコードを提示してください。
              </div>

              <div className="mt-2 small ">
                こちらの画面を提示する必要はありません<br></br>
                スタンプは購入日の翌日に付与されます
              </div>
            </React.Fragment>
          ) : null}
        </div>
      </div>

      <div className="d-flex justify-content-center">
        <div className={`px-sm-4 ${classes.widthControl}`}>
          <StampCard
            title="あなたのスタンプシート"
            stamps={
              stamps?.map((s) => {
                return {
                  id: s.id,
                  image: s.image,
                  categoryName:
                    campaign?.categories.find((c) => c.category === s.category)
                      ?.label || "",
                  isChecked:
                    s.isChecked ||
                    stampCard?.questionnaireStatus.isAnswered ||
                    false,
                  isAchieve: s.isAchieve,
                };
              }) || []
            }
            isCustomizable={false}
            isReplaceable={!isAchieved}
            onReplaceClick={handleTenantReplaceClick}
            onStampClick={(id) => handleStampClick(id)}
          ></StampCard>
        </div>
      </div>

      {!isAchieved ? (
        <React.Fragment>
          <div className="d-flex justify-content-center mt-3">
            <div className={`px-sm-4 ${classes.widthControl}`}>
              <AchievementStepper
                achieveCount={achieveCount || 0}
                checkedCount={checkedCount || 0}
                reward1={campaign?.labels.reward1 || ""}
                reward2={campaign?.labels.reward2 || ""}
                progress1={campaign?.labels.progress1 || ""}
                progress2={campaign?.labels.progress2 || ""}
              />
            </div>
          </div>

          <div
            className={`${classes.description} ${classes.campaign} mt-3 text-center`}
          >
            <TextDecoration sentences={campaign?.description} />

            <div className="mt-3">
              {campaign?.attention.map((attention, index) => {
                return (
                  <div key={index} className="small red-text">
                    {attention}
                  </div>
                );
              })}
            </div>
          </div>
        </React.Fragment>
      ) : (
        // スタンプコンプリート後
        <React.Fragment>
          <div className={`mt-3 d-flex justify-content-center`}>
            <div
              className={`${classes.messageRibbon} ${classes.widthControl} d-flex align-items-center justify-content-center`}
            >
              {campaign?.labels.progress2}
            </div>
          </div>
          <div className={`${classes.completeMessage} mt-3 text-center`}>
            <div className={classes.title}>おめでとうございます！</div>
            <div className="mt-2">
              <TextDecoration sentences={campaign?.labels.completeMessage} />
            </div>
          </div>
        </React.Fragment>
      )}

      <div className={`${classes.description}`}>
        {isAchieved && isChecked && !isAnswered ? (
          // アンケート未回答の場合
          <div className="d-flex justify-content-center">
            <div
              className={`p-3 my-4 text-center ${classes.questionnaireLeadPanel} ${classes.widthControl}`}
            >
              <div className={classes.title}>
                お買い物は楽しめましたか？
                <br />
                アンケートにご協力ください！
              </div>
              <MDBBtn
                className={`${classes.questionnaireButton} py-2`}
                color="pink"
                onClick={handleQuestionnaireButtonClick}
              >
                アンケートに答える
              </MDBBtn>
            </div>
          </div>
        ) : null}
        <div className={`${classes.note} text-center mt-4`}>
          キャンペーン中のご不明点に関しては
          <a
            href={campaign?.detail}
            className="mx-1"
            target="_blank"
            rel="noopener noreferrer"
          >
            こちら
          </a>
        </div>
      </div>

      {/* アンケート導入モーダル */}
      <MDBModal
        isOpen={isQuestionnaireIntroModalOpen}
        toggle={() => {}}
        centered
      >
        <MDBModalBody className="py-4">
          <div className={`text-center mb-3 ${classes.modalTitle}`}>
            {questionnaire?.contents.introTitle}
          </div>
          <div className={`text-center ${classes.leadMessage}`}>
            {questionnaire?.contents.introMessage.map((d, i) => {
              return (
                <React.Fragment key={`description-${i}`}>
                  {d} <br />
                </React.Fragment>
              );
            })}
          </div>
        </MDBModalBody>
        <MDBModalFooter
          className={`d-flex align-items-center justify-content-center ${classes.modalFooter}`}
        >
          <MDBRow>
            <MDBCol size="6" className="px-1 d-flex justify-content-center">
              <MDBBtn
                className={`${classes.closeButton} py-2 d-flex justify-content-center`}
                color="pink"
                onClick={handleCloseButtonClick}
              >
                閉じる
              </MDBBtn>
            </MDBCol>
            <MDBCol size="6" className="px-1 d-flex justify-content-center">
              <MDBBtn
                className={`${classes.questionnaireButton} py-2 d-flex justify-content-center`}
                color="pink"
                onClick={handleQuestionnaireButtonClick}
              >
                回答する
              </MDBBtn>
            </MDBCol>
          </MDBRow>
        </MDBModalFooter>
      </MDBModal>

      {/* テナント詳細モーダル */}
      <TenantDescriptionModal
        isOpenModal={isTenantDescriptionOpen}
        isActionButtonHidden
        onStampSelect={() => {}}
        onCloseButtonClick={() => {
          setIsTenantDescriptionOpen(false);
        }}
        tenantDescription={
          selectedStamp
            ? {
                image: selectedStamp.banner || selectedStamp.image,
                name: selectedStamp.name,
                description: selectedStamp.description || "",
                isRecommended: true,
                genre: selectedStamp.genre,
                floor: selectedStamp.area?.join("/"),
                businessHours: selectedStamp.businessHours,
                tel: selectedStamp.tel,
                budget: selectedStamp.budget,
              }
            : undefined
        }
      />
    </MDBContainer>
  );
};

export default StampCardPage;
